/**
 * Resolve the email address a respondent submitted on a form.
 *
 * Shared by the public submission routes: `save-response` (to dedupe/lookup the
 * response row) and `send-bulk-email` (to bind an anonymous confirmation send to
 * the address that was actually submitted). Keep the resolution order identical in
 * both places — a drift would let the confirmation gate disagree with the row it
 * is confirming. `getCandidateEmailFields` below powers the same resolution order
 * for the admin "Send To Field" dropdowns (`EmailEditorFields.tsx`,
 * `EmailBuilderPage.tsx`), so the two can't independently drift either.
 */

const EMAIL_FALLBACK_LABELS = ["Email", "email", "Email Address", "Email address", "Work email", "Work Email"] as const;

/**
 * Coerce an arbitrary form field value into a normalized (trimmed, lowercased) email,
 * or null when it doesn't look like one. Recurses through arrays and `{email|value|address}`
 * objects so structured field values resolve too.
 *
 * @param value - A raw form field value of unknown shape.
 * @returns The normalized email, or null when no email-like value is present.
 */
export const coerceEmailValue = (value: unknown): string | null => {
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized.includes("@") ? normalized : null;
  }

  if (typeof value === "number") {
    const normalized = String(value).trim().toLowerCase();
    return normalized.includes("@") ? normalized : null;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const email = coerceEmailValue(item);
      if (email) return email;
    }
  }

  if (value && typeof value === "object") {
    const candidate = value as Record<string, unknown>;
    return (
      coerceEmailValue(candidate.email) ??
      coerceEmailValue(candidate.value) ??
      coerceEmailValue(candidate.address) ??
      null
    );
  }

  return null;
};

/**
 * Whether a single form field is A canonical email question — by `id` (the seeded default email
 * field) or by `type` (a field explicitly typed "email"). For checking one already-identified
 * field (e.g. a specific column a staff member is editing); it does NOT rank id-matches over
 * type-matches, so don't use it to pick "the" email field out of a list that might contain
 * both — use {@link findCanonicalEmailField} for that instead.
 *
 * @param field - A form field's `id` and `type`.
 * @returns Whether this field is a canonical email question.
 */
export const isCanonicalEmailField = (field: { id: string; type: string }): boolean =>
  field.id === "email" || field.type === "email";

/**
 * Finds THE canonical email field out of a form's fields, preferring an `id === "email"` match
 * over a `type === "email"` match regardless of array order — a form could have both (e.g. a
 * renamed legacy field still typed "email" alongside the current seeded default), and the seeded
 * default should always win. Shared by every place that needs to single out one such field rather
 * than guess from a label, so they can't independently drift on what counts as canonical.
 *
 * @param formFields - The form's field definitions.
 * @returns The canonical email field, or undefined when the form has neither.
 */
export const findCanonicalEmailField = <T extends { id: string; type: string }>(formFields: T[]): T | undefined =>
  formFields.find((field) => field.id === "email") ?? formFields.find((field) => field.type === "email");

export interface EmailCandidateField {
  /** The field's label — `formData` is keyed by label, so this doubles as the lookup key. */
  value: string;
  label: string;
}

/**
 * Returns a form's candidate "email" fields, in the same priority order
 * `extractSubmittedEmail` resolves in: canonical (`id === "email"` then
 * `type === "email"`), then common labels, then any label mentioning "email".
 * Deduplicated by label. This is the single source of truth for "which fields
 * on this form could hold the submitter's email" — used both by
 * `extractSubmittedEmail`'s fallback tiers below and by the admin "Send To
 * Field" dropdowns, so the two can't independently drift.
 *
 * @param formFields - The form's field definitions.
 * @returns Candidate fields in priority order, deduplicated by label.
 */
export const getCandidateEmailFields = (
  formFields: Array<{ id: string; label: string; type: string }> | undefined
): EmailCandidateField[] => {
  if (!formFields?.length) return [];

  const seen = new Set<string>();
  const candidates: EmailCandidateField[] = [];
  const push = (field: { label: string }) => {
    if (seen.has(field.label)) return;
    seen.add(field.label);
    candidates.push({ value: field.label, label: field.label });
  };

  const canonical = findCanonicalEmailField(formFields);
  if (canonical) push(canonical);

  for (const label of EMAIL_FALLBACK_LABELS) {
    const match = formFields.find((field) => field.label === label);
    if (match) push(match);
  }

  for (const field of formFields) {
    if (field.label.toLowerCase().includes("email")) push(field);
  }

  return candidates;
};

/**
 * Find the submitter's email in a form response payload: prefer a configured
 * `sendToField` (matching the client's resolution order), then the canonical
 * `email` field (by id, then by type), then common email labels, then any field
 * whose label mentions "email".
 *
 * @param formData - The submitted response data keyed by field label.
 * @param formFields - The form's field definitions (used to locate the email field).
 * @param sendToField - The confirmation template's configured "send to" field label, if any.
 * @returns The normalized submitted email, or null when none is present.
 */
export const extractSubmittedEmail = (
  formData: Record<string, unknown>,
  formFields: Array<{ id: string; label: string; type: string }>,
  sendToField?: string | null
): string | null => {
  if (sendToField) {
    const email = coerceEmailValue(formData[sendToField]);
    if (email) return email;
  }

  const emailField = findCanonicalEmailField(formFields);
  if (emailField) {
    // A canonical match resolves unconditionally, even if its value doesn't
    // coerce to an email — an explicit canonical field should not silently
    // fall through to a lower-priority guess.
    return coerceEmailValue(formData[emailField.label]);
  }

  // Deliberately data-driven (scans formData directly), not field-driven via
  // getCandidateEmailFields: formData can carry a matching key with no
  // backing field definition (e.g. formFields is empty/stale relative to what
  // was actually submitted), and that key should still resolve. The tier
  // *order* still matches getCandidateEmailFields — both draw from
  // EMAIL_FALLBACK_LABELS — so the two can't diverge on priority, only on
  // whether an undeclared field counts as a candidate.
  for (const label of EMAIL_FALLBACK_LABELS) {
    const email = coerceEmailValue(formData[label]);
    if (email) return email;
  }

  for (const [key, value] of Object.entries(formData)) {
    if (!key.toLowerCase().includes("email")) continue;
    const email = coerceEmailValue(value);
    if (email) return email;
  }

  return null;
};

/**
 * Checks whether an email's domain is present in a form's configured allowlist.
 * An empty/missing allowlist means no restriction is configured, so any domain passes.
 *
 * @param email - The email address to check, or null when unresolved.
 * @param allowedDomains - The form's `allowed_email_domains` column value.
 * @returns Whether the email's domain is permitted.
 */
export const isEmailDomainAllowed = (email: string | null, allowedDomains: string[] | null | undefined): boolean => {
  const normalizedAllowed = (allowedDomains ?? []).map((d) => d.toLowerCase().trim()).filter(Boolean);
  if (normalizedAllowed.length === 0) return true;

  const domain = email?.split("@")[1]?.trim();
  return !!domain && normalizedAllowed.includes(domain);
};
